import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { ShoppingCart, MapPin, Plus, Trash2, CheckCircle2 } from "lucide-react";

/** Van Sales — mobile-first invoice-on-the-go for salesmen billing retailers
 * directly from the truck. Single screen: pick retailer → add lines → post →
 * moves on to next stop. */
export default function VanSales() {
  const [retailers, setRetailers] = useState([]);
  const [items, setItems] = useState([]);
  const [dists, setDists] = useState([]);
  const [distributor, setDistributor] = useState("");
  const [retailer, setRetailer] = useState("");
  const [lines, setLines] = useState([]);
  const [posted, setPosted] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/distribution/retailers").then((r) => setRetailers(r.data));
    api.get("/distribution/distributors").then((r) => setDists(r.data));
    api.get("/items").then((r) => setItems((r.data || []).slice(0, 500)));
  }, []);

  const shownRetailers = useMemo(() => retailers.filter((r) => !distributor || r.parent_party_id === distributor), [retailers, distributor]);
  const filteredItems = useMemo(() => {
    if (!q.trim()) return items.slice(0, 20);
    const n = q.toLowerCase();
    return items.filter((i) => (i.name || "").toLowerCase().includes(n) || (i.sku || "").toLowerCase().includes(n)).slice(0, 20);
  }, [items, q]);

  const addLine = (item) => {
    const existing = lines.find((l) => l.item_id === item.id);
    if (existing) return setLines(lines.map((l) => l.item_id === item.id ? { ...l, qty: l.qty + 1 } : l));
    setLines([...lines, { item_id: item.id, item_name: item.name, qty: 1, rate: item.sale_price || 0, discount: 0 }]);
  };
  const setLine = (i, patch) => setLines(lines.map((l, j) => j === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines(lines.filter((_, j) => j !== i));

  const total = lines.reduce((s, l) => s + (l.qty * l.rate - (l.discount || 0)), 0);

  const post = async () => {
    if (!distributor || !retailer) return toast.error("Pick distributor + retailer");
    if (!lines.length) return toast.error("Add at least one item");
    try {
      const r = await api.post("/distribution/secondary-sales", {
        distributor_id: distributor, retailer_id: retailer, lines,
      });
      setPosted((p) => [{ id: r.data.id, retailer_name: retailers.find((x) => x.id === retailer)?.name, total: r.data.total }, ...p].slice(0, 10));
      toast.success(`Posted — ₹${fmtINR(r.data.total)}`);
      setLines([]); setRetailer("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-3" data-testid="van-sales">
      <div className="max-w-md mx-auto space-y-3">
        <div className="bg-white rounded-sm p-3 shadow-sm">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-accent">
            <ShoppingCart className="w-3 h-3" /> Van Sales · Mobile
          </div>
          <div className="text-xl font-bold mt-0.5">Bill on the go</div>
        </div>

        {/* Pick distributor + retailer */}
        <Card className="p-3 rounded-sm">
          <label className="text-[10px] font-mono uppercase text-muted-foreground">Distributor</label>
          <Select value={distributor} onValueChange={(v) => { setDistributor(v); setRetailer(""); }}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="van-dist"><SelectValue placeholder="Pick" /></SelectTrigger>
            <SelectContent>{dists.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
          <label className="text-[10px] font-mono uppercase text-muted-foreground mt-3 block">Retailer</label>
          <Select value={retailer} onValueChange={setRetailer} disabled={!distributor}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="van-ret"><SelectValue placeholder={distributor ? "Pick" : "Choose distributor first"} /></SelectTrigger>
            <SelectContent>{shownRetailers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
          {retailer && <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {retailers.find((x) => x.id === retailer)?.address || "No address on file"}</div>}
        </Card>

        {/* Item picker */}
        {retailer && (
          <Card className="p-3 rounded-sm">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item / SKU…" className="rounded-sm mb-2" data-testid="van-item-search" />
            <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
              {filteredItems.map((i) => (
                <button key={i.id} onClick={() => addLine(i)} className="text-left p-2 rounded-sm bg-slate-100 hover:bg-accent/10 border hover:border-accent/40 text-xs" data-testid={`van-item-${i.id}`}>
                  <div className="font-semibold truncate">{i.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">₹{fmtINR(i.sale_price || 0)}</div>
                </button>
              ))}
              {filteredItems.length === 0 && <div className="col-span-2 text-xs text-muted-foreground italic p-4 text-center">No items</div>}
            </div>
          </Card>
        )}

        {/* Cart */}
        {lines.length > 0 && (
          <Card className="p-3 rounded-sm space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Cart · {lines.length}</div>
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-sm border-b pb-2 last:border-b-0">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-xs truncate">{l.item_name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">₹{l.rate} × {l.qty}{l.discount > 0 && ` −₹${l.discount}`}</div>
                </div>
                <Input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} className="w-14 h-8 rounded-sm text-right" />
                <button onClick={() => removeLine(i)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="flex justify-between items-baseline pt-2">
              <span className="text-xs text-muted-foreground">Total</span>
              <span className="text-2xl font-bold font-mono">{fmtINR(total)}</span>
            </div>
            <Button onClick={post} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white h-11 text-base" data-testid="van-post">
              Post secondary sale · ₹{fmtINR(total)}
            </Button>
          </Card>
        )}

        {/* Posted today */}
        {posted.length > 0 && (
          <Card className="p-3 rounded-sm">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-2">Posted this session · {posted.length}</div>
            {posted.map((p) => (
              <div key={p.id} className="flex items-center gap-2 py-1 text-xs border-b last:border-b-0">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                <span className="flex-1 truncate">{p.retailer_name}</span>
                <span className="font-mono">₹{fmtINR(p.total)}</span>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
